/**
 * Expression Evaluator for Conditional Formatting
 * Evaluates mathematical expressions with column references
 */

interface ExpressionContext {
  [key: string]: any; // Row data
}

/**
 * Evaluates a mathematical expression with column references
 * @param expression - Expression string (e.g., "profit - loss", "(revenue - expenses) / revenue * 100")
 * @param context - Row data object
 * @returns Calculated result
 */
export function evaluateExpression(
  expression: string,
  context: ExpressionContext
): number {
  try {
    if (!expression || typeof expression !== 'string') {
      return 0;
    }

    // Replace column references with actual values
    let evaluatedExpression = expression.trim();
    
    // First, handle nested properties (e.g., client.clientName)
    // Extract all possible nested property paths from context
    const getAllNestedPaths = (obj: any, prefix = ''): string[] => {
      const paths: string[] = [];
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const fullPath = prefix ? `${prefix}.${key}` : key;
          paths.push(fullPath);
          if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null) {
            paths.push(...getAllNestedPaths(obj[key], fullPath));
          }
        }
      }
      return paths;
    };
    
    const allPaths = getAllNestedPaths(context);
    // Sort by length (longest first) to match nested paths before simple keys
    const sortedPaths = allPaths.sort((a, b) => b.length - a.length);
    
    // Replace nested property paths first
    sortedPaths.forEach((path) => {
      const parts = path.split('.');
      let value: any = context;
      for (const part of parts) {
        value = value?.[part];
        if (value === undefined) break;
      }
      
      // Escape special regex characters in path
      const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match the path as a whole (not as separate words)
      const regex = new RegExp(`\\b${escapedPath}\\b`, 'g');
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        evaluatedExpression = evaluatedExpression.replace(regex, '0');
        return;
      }
      
      // Convert to number
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      const replacement = isNaN(num) ? '0' : String(num);
      
      evaluatedExpression = evaluatedExpression.replace(regex, replacement);
    });
    
    // Validate expression contains only safe characters
    const safePattern = /^[0-9+\-*/().\s]+$/;
    if (!safePattern.test(evaluatedExpression.replace(/\s/g, ''))) {
      console.error('Unsafe expression detected:', expression);
      return 0;
    }
    
    // Evaluate the expression safely using Function constructor
    // This is safer than eval() but still allows mathematical expressions
    const result = Function(`"use strict"; return (${evaluatedExpression})`)();
    
    return typeof result === 'number' && !isNaN(result) ? result : 0;
  } catch (error) {
    console.error('Expression evaluation error:', error, { expression, context });
    return 0; // Return 0 on error
  }
}

/**
 * Validates an expression string
 * @param expression - Expression to validate
 * @param availableColumns - List of available column names
 * @returns Validation result
 */
export function validateExpression(
  expression: string,
  availableColumns: string[]
): { valid: boolean; error?: string } {
  if (!expression || typeof expression !== 'string' || expression.trim() === '') {
    return { valid: false, error: 'Expression cannot be empty' };
  }

  // Check for dangerous patterns
  const dangerousPatterns = ['eval', 'Function', 'constructor', 'prototype', '__proto__'];
  const lowerExpression = expression.toLowerCase();
  for (const pattern of dangerousPatterns) {
    if (lowerExpression.includes(pattern)) {
      return { valid: false, error: `Invalid expression: contains "${pattern}"` };
    }
  }

  // Extract column references (words that could be column names)
  const columnPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
  const matches = expression.match(columnPattern) || [];
  const referencedColumns = matches.filter(m => {
    // Exclude JavaScript keywords and built-ins
    const keywords = ['Math', 'Number', 'parseFloat', 'parseInt', 'true', 'false', 'null', 'undefined', 'NaN', 'Infinity'];
    return !keywords.includes(m);
  });

  // Check if all referenced columns exist
  const invalidColumns = referencedColumns.filter(col => !availableColumns.includes(col));
  
  if (invalidColumns.length > 0) {
    return { 
      valid: false, 
      error: `Unknown columns: ${invalidColumns.join(', ')}` 
    };
  }

  return { valid: true };
}

/**
 * Applies an operator to compare two values
 */
function applyOperator(
  leftValue: any,
  rightValue: any,
  operator: string,
  value2?: any
): boolean {
  // Handle null/undefined
  if (leftValue === null || leftValue === undefined) {
    return operator === 'isEmpty';
  }

  // Convert to numbers if possible
  const leftNum = typeof leftValue === 'number' ? leftValue : parseFloat(String(leftValue));
  const rightNum = typeof rightValue === 'string' && !isNaN(parseFloat(rightValue))
    ? parseFloat(rightValue)
    : rightValue;

  switch (operator) {
    case 'greaterThan':
      return Number(leftNum) > Number(rightNum);
    case 'greaterThanOrEqual':
      return Number(leftNum) >= Number(rightNum);
    case 'lessThan':
      return Number(leftNum) < Number(rightNum);
    case 'lessThanOrEqual':
      return Number(leftNum) <= Number(rightNum);
    case 'equals':
      // For string comparisons, do case-insensitive comparison
      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        return leftValue.toLowerCase() === rightValue.toLowerCase();
      }
      // For numeric comparisons
      if (typeof leftNum === 'number' && !isNaN(leftNum) && typeof rightNum === 'number' && !isNaN(rightNum)) {
        return Number(leftNum) === Number(rightNum);
      }
      // Fallback to strict equality
      return leftValue === rightValue;
    case 'notEquals':
      // For string comparisons, do case-insensitive comparison
      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        return leftValue.toLowerCase() !== rightValue.toLowerCase();
      }
      // For numeric comparisons
      if (typeof leftNum === 'number' && !isNaN(leftNum) && typeof rightNum === 'number' && !isNaN(rightNum)) {
        return Number(leftNum) !== Number(rightNum);
      }
      // Fallback to strict inequality
      return leftValue !== rightValue;
    case 'between':
      return Number(leftNum) >= Number(rightValue) && 
             Number(leftNum) <= Number(value2);
    case 'contains':
      return String(leftValue).toLowerCase().includes(String(rightValue).toLowerCase());
    case 'isEmpty':
      return leftValue === null || leftValue === undefined || leftValue === '';
    case 'isNotEmpty':
      return leftValue !== null && leftValue !== undefined && leftValue !== '';
    default:
      return false;
  }
}

/**
 * Evaluates a condition against row data
 * Supports both column-based and expression-based conditions
 */
export function evaluateCondition(
  condition: {
    type: 'column' | 'expression';
    columnKey?: string;      // For 'column' type
    expression?: string;     // For 'expression' type
    operator: string;
    value: any;
    value2?: any;
  },
  row: Record<string, any>,
  columnKey?: string // For column scope conditions
): boolean {
  try {
    let leftValue: any;
    
    if (condition.type === 'column') {
      // OPTION 1: Single Column - get value directly from column
      const keyToUse = condition.columnKey || columnKey;
      if (!keyToUse) return false;
      
      leftValue = row[keyToUse];
      
      // Handle nested properties (e.g., client.clientName)
      if (leftValue === undefined && keyToUse.includes('.')) {
        const parts = keyToUse.split('.');
        let current: any = row;
        for (const part of parts) {
          current = current?.[part];
          if (current === undefined) break;
        }
        leftValue = current;
      }
      
      // Handle computed columns that don't exist in raw data
      // For "status" column in invoices, compute it from paymentTotal and invoiceAmount
      if (leftValue === undefined && keyToUse === 'status') {
        const invoiceAmount = parseFloat(String(row.invoiceAmount || 0));
        const paymentTotal = row.paymentTotal !== null ? parseFloat(String(row.paymentTotal)) : null;
        
        if (paymentTotal === null) {
          leftValue = 'Unpaid';
        } else if (paymentTotal < invoiceAmount) {
          leftValue = 'Partial';
        } else if (paymentTotal >= invoiceAmount) {
          leftValue = 'Paid';
        } else {
          leftValue = 'Unpaid';
        }
      }
      
      // Handle null/undefined
      if (leftValue === null || leftValue === undefined) {
        return condition.operator === 'isEmpty';
      }
      
      // Convert to number if it's a numeric string
      if (typeof leftValue === 'string' && !isNaN(parseFloat(leftValue))) {
        leftValue = parseFloat(leftValue);
      }
      
    } else if (condition.type === 'expression') {
      // OPTION 2: Expression - calculate from multiple columns
      if (!condition.expression) return false;
      
      leftValue = evaluateExpression(condition.expression, row);
      
    } else {
      return false;
    }
    
    return applyOperator(leftValue, condition.value, condition.operator, condition.value2);
    
  } catch (error) {
    console.error('Condition evaluation error:', error, { condition, row });
    return false;
  }
}

